import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  checkHotelRegistrationLimit,
  fetchPropertyTypes,
  fetchGenders,
  fetchCountries,
  fetchNationalities,
  registerAgentHotel,
  validateCityZip,
  validateNicDob,
  submitAgentLimitIncreaseRequest,
} from "../services/agentService";
import Swal from "sweetalert2";
import { FaArrowLeft, FaHotel, FaIdCard, FaFileAlt, FaCheckCircle, FaCloudUploadAlt, FaMapMarkerAlt, FaUser } from "react-icons/fa";
import "./Register.css"; // Reuse premium styles
import NavUser from "../components/NavUser";

function AgentHotelRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isAgentApproved, setIsAgentApproved] = useState(true);
  const [approvalMessage, setApprovalMessage] = useState("");

  // Dropdowns
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [nationalities, setNationalities] = useState([]);

  // Form fields
  const [form, setForm] = useState({
    businessName: "",
    address: "",
    city: "",
    zipCode: "",
    businessOwnerName: "",
    phoneNumber: "",
    businessEmail: "",
    nationalIdNumber: "",
    dateOfBirth: "",
    propertyTypeId: "",
    genderId: "",
    countryId: "",
    nationalityId: "",
  });

  // Files
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [waterFront, setWaterFront] = useState(null);
  const [waterBack, setWaterBack] = useState(null);
  const [qualificationDoc, setQualificationDoc] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const limit = await checkHotelRegistrationLimit();
        if (!limit.allowed && limit.message?.toLowerCase().includes("pending")) {
          setIsAgentApproved(false);
          setApprovalMessage(limit.message);
        }

        setPropertyTypes(await fetchPropertyTypes());
        setGenders(await fetchGenders());
        const allCountries = await fetchCountries();
        const slCountries = allCountries.filter((c) =>
          c.name.toLowerCase().includes("sri lanka")
        );
        setCountries(slCountries);

        const allNats = await fetchNationalities();
        const slNats = allNats.filter((n) =>
          n.name.toLowerCase().includes("sri lanka")
        );
        setNationalities(slNats);

        setForm((prev) => ({
          ...prev,
          countryId: slCountries[0]?.id || "",
          nationalityId: slNats[0]?.id || prev.nationalityId,
        }));
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = (setter) => (e) => setter(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAgentApproved) {
      Swal.fire({
        title: "Account Pending",
        text: approvalMessage || "Your agent account is pending administrator approval.",
        icon: "warning"
      });
      return;
    }

    try {
      setLoading(true);

      const limit = await checkHotelRegistrationLimit();
      if (!limit.allowed) {
        setLoading(false);
        if (limit.hasPendingRequest) {
          Swal.fire({
            title: "Request Under Review",
            text: "You have reached your hotel registration limit. Your request for a limit increase is currently under administrator review.",
            icon: "info"
          });
          return;
        }

        const promptResult = await Swal.fire({
          title: "Limit Reached",
          text: limit.message || `You have reached your hotel registration limit (${limit.limit}). Would you like to request an increase?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Request Limit Increase",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#2563eb",
        });

        if (promptResult.isConfirmed) {
          const { value: reason } = await Swal.fire({
            title: "Request Hotel Limit Increase",
            input: "textarea",
            inputLabel: "Please provide a reason for requesting more hotel registrations:",
            inputPlaceholder: "e.g., Expanding partner hotels in Kandy...",
            showCancelButton: true,
            confirmButtonText: "Submit Request",
            confirmButtonColor: "#2563eb",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "Please enter a reason.";
            },
          });

          if (reason) {
            try {
              await submitAgentLimitIncreaseRequest("hotel", reason.trim());
              Swal.fire("Request Submitted", "Your limit increase request has been sent to the administrator.", "success");
            } catch (err) {
              Swal.fire("Error", err.response?.data?.error || "Failed to submit request.", "error");
            }
          }
        }
        return;
      }

      const isCityZipValid = await validateCityZip(form.city, form.zipCode);
      if (!isCityZipValid) {
        Swal.fire({ title: "Invalid Location", text: "City and Zip code do not match.", icon: "error" });
        setLoading(false);
        return;
      }

      const isNicDobValid = await validateNicDob(form.nationalIdNumber, form.dateOfBirth);
      if (!isNicDobValid) {
        Swal.fire({ title: "Validation Error", text: "NIC number does not match the Date of Birth.", icon: "error" });
        setLoading(false);
        return;
      }

      const fd = new FormData();
      fd.append("full_name", form.businessOwnerName);
      fd.append("phone_number", form.phoneNumber);
      fd.append("email", form.businessEmail);
      fd.append("country_id", form.countryId);
      fd.append("gender_id", form.genderId);
      fd.append("nationality_id", form.nationalityId);
      fd.append("dob", form.dateOfBirth);
      fd.append("national_id_number", form.nationalIdNumber);
      fd.append("name", form.businessName);
      fd.append("address", form.address);
      fd.append("city", form.city);
      fd.append("zip_code", form.zipCode);
      fd.append("property_type_id", form.propertyTypeId);

      if (nicFront) fd.append("nic_front", nicFront);
      if (nicBack) fd.append("nic_back", nicBack);
      if (waterFront) fd.append("water_bill_front", waterFront);
      if (waterBack) fd.append("water_bill_back", waterBack);
      if (qualificationDoc) fd.append("business_registration_or_qualifications", qualificationDoc);

      const res = await registerAgentHotel(fd);

      if (res.status === 201) {
        Swal.fire({
          title: "Registration Submitted!",
          text: "The hotel owner account has been registered and is pending admin approval.",
          icon: "success",
          confirmButtonColor: "#3b82f6",
        }).then(() => navigate(-1));
      } else {
        Swal.fire({ title: "Registration Failed", text: res.data?.message || "Failed to register hotel.", icon: "error" });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error", text: "Something went wrong during submission.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Partner Onboarding</h1>
          <p>Register new hotel partners to expand the Soloway network.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        <div className="premium-glass-card">
          <div className="card-header-premium">
            <button onClick={() => navigate(-1)} className="btn-back-minimal">
              <FaArrowLeft />
            </button>
            <div className="header-titles">
              <h2>Register New Hotel</h2>
              <p>Onboard a professional hospitality partner</p>
            </div>
          </div>

          {!isAgentApproved && (
            <div style={{
              background: "rgba(245, 158, 11, 0.15)",
              border: "1px solid #f59e0b",
              color: "#d97706",
              padding: "16px 20px",
              borderRadius: "12px",
              marginBottom: "25px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontWeight: "600"
            }}>
              <span style={{ fontSize: "24px" }}>⏳</span>
              <span>{approvalMessage || "Your Travel Agent account is pending administrator approval. You cannot register hotels yet."}</span>
            </div>
          )}

          <form className="premium-form-layout" onSubmit={handleSubmit}>
            <div className="form-grid-2">
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaHotel /> <span>Business Details</span>
                </div>
                
                <div className="premium-field">
                  <label>Business Name</label>
                  <input name="businessName" value={form.businessName} onChange={handleChange} placeholder="Hotel or Resort Name" required />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Property Type</label>
                    <select name="propertyTypeId" value={form.propertyTypeId} onChange={handleChange} required>
                      <option value="">Select Type</option>
                      {propertyTypes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="City" required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Zip Code</label>
                    <input name="zipCode" value={form.zipCode} onChange={handleChange} placeholder="Zip" required />
                  </div>
                  <div className="premium-field">
                    <label>Address</label>
                    <input name="address" value={form.address} onChange={handleChange} placeholder="Full Address" required />
                  </div>
                </div>

                <div className="section-header-minimal" style={{ marginTop: '30px' }}>
                  <FaUser /> <span>Owner Information</span>
                </div>

                <div className="premium-field">
                  <label>Business Owner Full Name</label>
                  <input name="businessOwnerName" value={form.businessOwnerName} onChange={handleChange} placeholder="Legal Name" required />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Business Email</label>
                    <input type="email" name="businessEmail" value={form.businessEmail} onChange={handleChange} placeholder="contact@hotel.com" required />
                  </div>
                  <div className="premium-field">
                    <label>Phone Number</label>
                    <input name="phoneNumber" value={form.phoneNumber} onChange={handleChange} placeholder="+94..." required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Country</label>
                    <select name="countryId" value={form.countryId} onChange={handleChange} required>
                      <option value="">Select</option>
                      {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>Nationality</label>
                    <select name="nationalityId" value={form.nationalityId} onChange={handleChange} required>
                      <option value="">Select</option>
                      {nationalities.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>National ID Number</label>
                    <input name="nationalIdNumber" value={form.nationalIdNumber} onChange={handleChange} required />
                  </div>
                  <div className="premium-field">
                    <label>Date of Birth</label>
                    <input type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} required />
                  </div>
                </div>

                <div className="premium-field">
                  <label>Gender</label>
                  <select name="genderId" value={form.genderId} onChange={handleChange} required>
                    <option value="">Select</option>
                    {genders.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-column">
                <div className="section-header-minimal">
                  <FaFileAlt /> <span>Verification Documents</span>
                </div>

                <div className="upload-grid-premium">
                  <div className="premium-upload-item">
                    <label>Owner ID Front</label>
                    <label className={`upload-zone ${nicFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setNicFront)} required />
                      <FaCloudUploadAlt />
                      <span>{nicFront ? nicFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Owner ID Back</label>
                    <label className={`upload-zone ${nicBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setNicBack)} required />
                      <FaCloudUploadAlt />
                      <span>{nicBack ? nicBack.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Front)</label>
                    <label className={`upload-zone ${waterFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setWaterFront)} required />
                      <FaCloudUploadAlt />
                      <span>{waterFront ? waterFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Back)</label>
                    <label className={`upload-zone ${waterBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*" onChange={handleFile(setWaterBack)} required />
                      <FaCloudUploadAlt />
                      <span>{waterBack ? waterBack.name : "Choose File"}</span>
                    </label>
                  </div>
                </div>

                <div className="premium-field full-width" style={{ marginTop: '20px' }}>
                  <label>Registration Doc (Optional)</label>
                  <label className={`upload-zone-wide ${qualificationDoc ? 'active' : ''}`}>
                    <input type="file" onChange={handleFile(setQualificationDoc)} />
                    <FaCloudUploadAlt />
                    <span>{qualificationDoc ? qualificationDoc.name : "Upload supporting registration (PDF/Image)"}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="action-row-centered">
              <button type="submit" className="btn-premium-action" disabled={loading}>
                {loading ? (
                  <span className="loader-dots">Processing...</span>
                ) : (
                  <>
                    <FaCheckCircle /> Register Hotel Owner
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgentHotelRegister;
